import { Check, ChevronDown, Search } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import {
  closePanelFloatingUi,
  panelFloatingUiAttribute,
  panelFloatingUiCloseEvent,
  panelSelectTriggerClass,
} from "./PanelSelect";

export function SearchableFloatingSelect<T extends string>({
  label,
  value,
  options,
  onChange,
  onFocus,
  searchPlaceholder = "Search",
  emptyMessage = "No matches.",
  triggerClassName,
  className,
}: {
  label?: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
  onFocus?: () => void;
  searchPlaceholder?: string;
  emptyMessage?: string;
  triggerClassName?: string;
  className?: string;
}) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedValue, setHighlightedValue] = useState<T>(value);
  const [position, setPosition] = useState<{ left: number; top: number } | undefined>(undefined);

  const selectedLabel = options.find(option => option.value === value)?.label ?? value;

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return options;
    }
    return options.filter(option => option.label.toLowerCase().includes(needle));
  }, [options, query]);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(undefined);
      return;
    }

    function updatePosition(): void {
      const anchor = triggerRef.current;
      const panel = panelRef.current;
      if (!anchor || !panel) {
        return;
      }

      const rect = anchor.getBoundingClientRect();
      const margin = 8;
      const gap = 4;
      const width = panel.offsetWidth;
      const height = panel.offsetHeight;

      let left = rect.right - width;
      left = Math.min(Math.max(margin, left), Math.max(margin, window.innerWidth - width - margin));

      const belowTop = rect.bottom + gap;
      const aboveTop = rect.top - height - gap;
      const fitsBelow = belowTop + height <= window.innerHeight - margin;
      const top = fitsBelow ? belowTop : Math.max(margin, aboveTop);

      setPosition({ left, top });
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, rows.length]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setQuery("");
    setHighlightedValue(value);
    requestAnimationFrame(() => {
      searchRef.current?.focus();
      searchRef.current?.select();
      scrollRowIntoView(value);
    });
  }, [open, value]);

  useEffect(() => {
    if (!open || rows.some(option => option.value === highlightedValue)) {
      return;
    }
    setHighlightedValue(rows[0]?.value ?? value);
  }, [highlightedValue, open, rows, value]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function handlePointerDown(event: PointerEvent): void {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    }

    function handleKeyDown(event: KeyboardEvent): void {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        moveHighlight(1);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        moveHighlight(-1);
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        commitValue(highlightedValue);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
      }
    }

    function handleCloseRequest(): void {
      setOpen(false);
    }

    function handleWheel(event: WheelEvent): void {
      const target = event.target;
      if (target instanceof Node && (triggerRef.current?.contains(target) || panelRef.current?.contains(target))) {
        return;
      }
      if (target instanceof Element && target.closest(`[${panelFloatingUiAttribute}]`)) {
        return;
      }
      setOpen(false);
    }

    window.addEventListener("pointerdown", handlePointerDown, { capture: true });
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener(panelFloatingUiCloseEvent, handleCloseRequest);
    window.addEventListener("wheel", handleWheel, { capture: true, passive: true });
    return () => {
      window.removeEventListener("pointerdown", handlePointerDown, { capture: true });
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener(panelFloatingUiCloseEvent, handleCloseRequest);
      window.removeEventListener("wheel", handleWheel, { capture: true });
    };
  }, [highlightedValue, open, rows]);

  function commitValue(next: T): void {
    onChange(next);
    setOpen(false);
  }

  function scrollRowIntoView(optionValue: T): void {
    const row = rowRefs.current.get(optionValue);
    const list = listRef.current;
    if (!row || !list) {
      return;
    }
    const rowTop = row.offsetTop;
    const rowBottom = rowTop + row.offsetHeight;
    if (rowTop < list.scrollTop) {
      list.scrollTop = rowTop;
    } else if (rowBottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = rowBottom - list.clientHeight;
    }
  }

  function moveHighlight(delta: number): void {
    if (rows.length === 0) {
      return;
    }
    const currentIndex = Math.max(0, rows.findIndex(option => option.value === highlightedValue));
    const nextIndex = Math.min(rows.length - 1, Math.max(0, currentIndex + delta));
    const next = rows[nextIndex];
    if (!next) {
      return;
    }
    setHighlightedValue(next.value);
    requestAnimationFrame(() => scrollRowIntoView(next.value));
  }

  const control = (
    <div
      className={className}
      onPointerDownCapture={onFocus}
      onPointerDown={event => event.stopPropagation()}
      onFocusCapture={onFocus}
    >
      <button
        ref={triggerRef}
        type="button"
        className={cn(
          "flex h-8 w-full items-center justify-between gap-2 rounded-md border px-2 text-left text-[12px] outline-none focus-visible:border-sky-500/40",
          panelSelectTriggerClass,
          triggerClassName,
        )}
        onClick={() => {
          if (open) {
            setOpen(false);
            return;
          }
          closePanelFloatingUi();
          setOpen(true);
        }}
      >
        <span className="min-w-0 truncate">{selectedLabel}</span>
        <ChevronDown className="size-3.5 shrink-0 text-slate-500" aria-hidden="true" />
      </button>
      {open ? createPortal(
        <div
          ref={panelRef}
          className="fixed z-[70] w-72 rounded-md border border-white/10 bg-[#242a31] p-3 text-[12px] text-slate-300 shadow-2xl shadow-black/40"
          style={position ? { left: position.left, top: position.top } : { left: 0, top: 0, visibility: "hidden" }}
          onPointerDown={event => event.stopPropagation()}
          onContextMenu={event => event.preventDefault()}
          onWheel={event => event.stopPropagation()}
          {...{ [panelFloatingUiAttribute]: "" }}
        >
          <div className="relative mb-2">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-slate-500" aria-hidden="true" />
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              onKeyDown={event => {
                if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                  event.preventDefault();
                  event.stopPropagation();
                  moveHighlight(event.key === "ArrowDown" ? 1 : -1);
                }
              }}
              placeholder={searchPlaceholder}
              className="h-8 w-full rounded border border-white/10 bg-white/[0.04] pl-7 pr-2 text-[12px] text-slate-100 outline-none placeholder:text-slate-500 focus:border-sky-500/40"
            />
          </div>
          <div ref={listRef} className="max-h-72 overflow-y-auto rounded border border-white/10">
            {rows.length === 0 ? (
              <div className="px-2 py-3 text-center text-[11px] text-slate-500">{emptyMessage}</div>
            ) : rows.map(option => {
              const selected = option.value === value;
              const highlighted = option.value === highlightedValue;
              return (
                <button
                  key={option.value}
                  ref={element => {
                    if (element) {
                      rowRefs.current.set(option.value, element);
                    } else {
                      rowRefs.current.delete(option.value);
                    }
                  }}
                  type="button"
                  className={cn(
                    "flex h-9 w-full items-center gap-2 px-2 text-left text-[12px] hover:bg-white/[0.06]",
                    highlighted ? "bg-sky-500/20 text-sky-100" : selected ? "bg-sky-500/15 text-sky-100" : "text-slate-200",
                  )}
                  onMouseEnter={() => setHighlightedValue(option.value)}
                  onClick={() => commitValue(option.value)}
                  title={option.label}
                >
                  <span className="grid size-4 shrink-0 place-items-center text-sky-300">
                    {selected ? <Check className="size-3.5" aria-hidden="true" /> : null}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  );

  if (!label) {
    return control;
  }

  return (
    <label className="space-y-1">
      <span className="text-[11px] text-slate-500">{label}</span>
      {control}
    </label>
  );
}
